<script setup lang="ts">
import type * as pf from '@amirada/lib-shared'
import { NodeType } from '@amirada/lib-shared'
import type * as tp from '@base/utils/types'
import type * as pfWilo from '@amirada/lib-wilo-shared'
import { ExtCmdType, PARAM } from '@amirada/lib-wilo-shared'
import { useCartStore } from '@base/stores/cartStore'
import { ECarts, EScreenSize } from '@base/utils/constants'
import {
  ut_addUrlParams,
  ut_getText,
  ut_isEmbedded,
  ut_isPrint,
  ut_isScreenSize,
  ut_navigateNewRoute,
} from '@base/utils/utils'
import {
  ut_wilo_compareTablePrintGroups,
  ut_wilo_getCurveId,
  ut_wilo_getExtraKeys,
  ut_wilo_getSpecialPageText,
  ut_wilo_removeItemFromCart,
  ut_wilo_setAdessoStorage,
  ut_wilo_showPriceList,
} from '~/utils/utils'
import {
  ut_wilo_cartContent,
  ut_wilo_contentLabel,
} from '~/utils/priceList'
import type { ECartContentType } from '~/utils/constants'
import type { IStoredProject } from '~/utils/types'
import { def_cartContentMeta, def_printCompareGroupSize, ECartIconAr, ECompareType, ECartUserDefined } from '~/utils/constants'
import type { TabsItem } from '#ui/types'
import { usePfinderAPI } from '~/composables/pfinderAPI'
import { usePriceListProject } from '~/composables/priceListProject'
import { cartRowKey, usePriceListCart } from '~/composables/priceListCart'
import { useAppIndexStore } from '@base/stores/indexStore'
import type { AccordionItem } from '@nuxt/ui'

const p = defineProps<{
  cart: ECarts
  isInternal: boolean
  realCountry: string
  countryLanguage: string
}>()

const indexStore = useAppIndexStore()
const cartStore = useCartStore()
const pfinderAPI = usePfinderAPI()
const runTimeConfig = useRuntimeConfig()

const isCatalog: boolean = p.cart === ECarts.ct_catalogue_create
const isPriceList: boolean = p.cart === ECarts.ct_price_list_create
const priceList = usePriceListCart(p.cart)
const isLcc: boolean = p.cart === ECarts.ct_custom_1
const isCompare: boolean = p.cart === ECarts.ct_product_compare
const isDownloads: boolean = p.cart === ECarts.ct_download_list
const isFavorites: boolean = p.cart === ECarts.ct_favourites
const isSortView: Ref<boolean> = ref(false)
const dragInProgress: Ref<boolean> = ref(false)
const shareLink: Ref<string> = ref('')
const cartStorageId: Ref<string> = ref('')
/*
 * The price list is organised in PROJECTS — which one is open, opening and closing it, the lock and
 * the name, the save state: all of that is usePriceListProject. Idle for every other cart.
 */
const {
  currentProject,
  showProjects,
  saveLabel,
  opening,
  nameAsk,
  rename,
  setEditable,
  confirmName,
  openProject,
  releaseProject,
  openSaved,
  closeProject,
  openFromUrl,
} = usePriceListProject(p.cart)
const selectedTab: Ref<keyof typeof ECompareType> = ref(getActiveCompareTab(true))
const selectedAccordion: Ref<keyof typeof ECompareType> = ref(getActiveCompareTab(false))
const showModalPdf: Ref<boolean> = ref(false)
const lccData: Ref<pfWilo.ILccData> = ref({})
const compareTable: Ref<Record<string, pfWilo.TableNode | undefined>> = ref({})
const dutyPointInput: Ref<pfWilo.IDutyPointInput> = ref({})
const extraKeys: Ref<string[]> = ref([])
const compactView: Ref<boolean | undefined> = ref(undefined)

onBeforeMount(async () => {
  // If param12 is set load list by UUID and update cart info
  if (indexStore.state.paramData[PARAM.cartStorageId]) {
    await loadList(indexStore.state.paramData[PARAM.cartStorageId])
    // If not print redirect to url without cartStorageID
    if (!ut_isPrint()) {
      ut_navigateNewRoute('', {}, '', false, { noCustom: true })
    }
  }
  // LCC starts empty every time. It can be emptied here because its page (`custom`) is not in
  // `ut_isCartPage`, so nothing refreshes its cart after the stored state is restored.
  if (isLcc) {
    cartStore.setEmptyCart(p.cart)
  }
  // The price list takes the project to open from the url (see usePriceListProject#openFromUrl).
  await openFromUrl()
  // Start compare
  if (isCompare) {
    // Dutypoint in URL
    if (indexStore.state.paramData[PARAM.spaixSettings]) {
      dutyPointInput.value = JSON.parse(indexStore.state.paramData[PARAM.spaixSettings])
    }
    await compareData()
  }
})

// Watch change compare items and run new compare data
watch(() => cartStore.getCartCount (p.cart), async () => {
  if (isCompare) {
    await compareData()
  }
})

async function clickPDF() {
  if (isCompare) {
    cartStorageId.value = await saveList()
  }
  showModalPdf.value = true
}

async function compareData() {
  await Promise.all([
    callCompare(false),
    callCompare(true),
  ])
}

async function callCompare(takeProducts: boolean) {
  const type: ECompareType = takeProducts ? ECompareType.products : ECompareType.series
  const data: tp.IFetchData = {
    usePost: true,
  }
  const cart = cartStore.getCart(p.cart)
  const filteredCart: pf.CartItem[] = cart.filter(i => takeProducts ? (i.nodeType !== NodeType.nt_Configuration) : i.nodeType === NodeType.nt_Configuration)
  if (filteredCart.length > 0) {
    const args: pfWilo.ExtCmd<ExtCmdType.TABLE_COMPARE>['arg'] = {
      cart: cartStore.stripCart(filteredCart, true),
      tmpl: takeProducts ? 'TCT' : 'SCT',
    }
    const res = await pfinderAPI.fetchData(ExtCmdType.TABLE_COMPARE, args, data)

    if (res && res.result && res.result) {
      compareTable.value[type] = res.result
      if (!ut_isPrint() && takeProducts && res.result.header && res.result.nodes) {
        extraKeys.value = ut_wilo_getExtraKeys(res.result.header)
      }
    }
  }
  else {
    compareTable.value[type] = undefined
  }
}

// Compare has 2 tabs
function getCompareTabItems(): TabsItem[] | AccordionItem[] {
  return [{
    value: ECompareType.series,
    label: ut_getText('txt_selection-series') + ' (' + cartConfigs().length + ')' }, {
    value: ECompareType.products,
    label: ut_getText('txt_selection-products') + ' (' + (cartStore.getCartCount(p.cart) - cartConfigs().length) + ')' },
  ]
}

// Add tab param to URL
function changeCompareTab() {
  selectedAccordion.value = selectedTab.value
  ut_navigateNewRoute('', { tab: selectedTab.value }, '', false)
}

// Add tab param to URL
function changeCompareAccordion() {
  if (selectedAccordion.value) {
    selectedTab.value = selectedAccordion.value
  }
  ut_navigateNewRoute('', { tab: selectedAccordion.value || '' }, '', false)
}

// Set selected tab or products tab if no series
function getActiveCompareTab(useDefault: boolean): keyof typeof ECompareType {
  if (!isCompare) { return ECompareType.series }
  let res: keyof typeof ECompareType = useDefault ? ECompareType.series : ECompareType.none
  if (indexStore.state.paramData.tab) {
    res = indexStore.state.paramData.tab as keyof typeof ECompareType
  }
  else if (useDefault) {
    if (cartConfigs().length === 0 && cartStore.getCartCount(p.cart) > 0) {
      res = ECompareType.products
    }
  }
  return res
}

// Path to products
function getCatalogPath() {
  if (import.meta.client) {
    let res: string = window.location.href
    if (ut_isEmbedded()) {
      res = res.substring(0, res.lastIndexOf('/'))
    }
    else {
      res = res.substring(0, res.lastIndexOf('/'))
      res = res.substring(0, res.lastIndexOf('/'))
      res += '/root13'
    }

    res += '/8ae2819c8e9774cf018ea36fa2c86c6b?rawurl=1'
    return res
  }
}

// Get cart configs, use amount to set the number of products
function cartConfigs(includeMarkers: boolean = false): pf.CartItem[] {
  const cartConfigItems: pf.CartItem[] = []
  for (const item of cartStore.getCart(p.cart)) {
    const isConfig: boolean = item.nodeType === NodeType.nt_Configuration
    // Local content markers (chapter/…) are their own rows too (only when asked, e.g. for sorting).
    const isMarker: boolean = includeMarkers && !!ut_wilo_cartContent(item)
    if (isConfig || isMarker) {
      const cartItem: pf.CartItem = { ...item }
      cartItem.amount = 0
      // Markers carry their text in userDefined, not desc → fill the sort copy's desc for display
      // (PDF shows its filename, the others their label in the app's language). The real cart item
      // stays untouched. The pdf takes its marker texts in ITS language instead — see ModalPDF.
      if (isMarker) {
        const content = ut_wilo_cartContent(item)
        cartItem.desc = content?.name || ut_wilo_contentLabel(content, indexStore.state.paramData.lcda)
      }
      cartConfigItems.push(cartItem)
    }
    else {
      // Count a product on its PARENT config (config.nodeName) — not its own nodeName, which never
      // matches a config → amount stayed 0 → configs moved without their products when sorting.
      const parentName: string = item.config?.nodeName || ''
      const index: number = cartConfigItems.findIndex(i => i.nodeName === parentName)
      if (cartConfigItems[index]) {
        cartConfigItems[index].amount = (cartConfigItems[index]?.amount || 0) + 1
      }
    }
  }
  return cartConfigItems
}

// Empty cart and set adesso storage
function emptyCart() {
  cartStore.setEmptyCart(p.cart)
  if (p.cart) {
    ut_wilo_setAdessoStorage(p.cart, 0, p.realCountry)
  }
}

// Toggle between normal and sort view. Compare re-reads its tables on the way back: the order changed.
async function toggleSortView(event: boolean) {
  isSortView.value = event
  if (!event && isCompare) {
    await compareData()
  }
}

/*
 * What goes into the PDF. A price-list row can be hidden: it stays in the cart, in its counters and
 * in the sort view, and only the print drops it. The flag sits on EVERY affected row (the price list
 * marks the whole subtree, see CartSeriesPriceList), so nothing here has to know the hierarchy.
 *
 * Only the config/marker rows are filtered, and that is the complete set: a price list holds series
 * and structure, no single products (confirmed 24.08.2026). The `:cart` prop next to it carries the
 * product rows of the other carts and is deliberately left alone — it feeds every cart and several
 * paths inside ModalPDF.
 */
function cartConfigsForPdf(): pf.CartItem[] {
  return cartConfigs(isPriceList).filter(item => !item.userDefined?.[ECartUserDefined.cart_us_hidden])
}

// Get items for sorting
function sortItems(): pf.CartItem[] {
  if (!isCompare) {
    // Price list also sorts its content markers (chapter/…), so include them there.
    return cartConfigs(isPriceList)
  }
  else {
    return cartStore.getCart(p.cart).filter(i => (selectedTab.value === ECompareType.series ? (i.nodeType === NodeType.nt_Configuration) : (i.nodeType !== NodeType.nt_Configuration)))
  }
}

// Index of a cart row by its key (cartRowKey): the price list may hold the same series twice, so the
// namePath alone would find the first copy; for every other cart the key IS the namePath.
function realIndex(row: pf.CartItem | undefined): number {
  if (!row) { return -1 }
  const key: string = cartRowKey(row)
  return cartStore.getCart(p.cart).findIndex(i => cartRowKey(i) === key)
}

// Sort view: content markers use the same colour/icon as the price-list view (shared meta).
function sortRowColor(item: pf.CartItem): string {
  const type: ECartContentType | undefined = ut_wilo_cartContent(item)?.type
  return type ? def_cartContentMeta[type].color : 'bg-gunmetal-5'
}

function sortRowIcon(item: pf.CartItem): string {
  const type: ECartContentType | undefined = ut_wilo_cartContent(item)?.type
  return type ? def_cartContentMeta[type].icon : ''
}

// Drag & drop for sorting start
function CompanyStartDrag(event: DragEvent, indexFrom: number, itemsAdd: number): void {
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move'
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('index', indexFrom.toString())
    event.dataTransfer.setData('itemsAdd', itemsAdd.toString())
  }
}

// Drag & drop for sorting on enter
function CompanyOnEnter(event: DragEvent | undefined, indexTo: number, itemsAddTo: number, indexFrom: number, itemsAdd: number): void {
  if (event?.dataTransfer) {
    indexFrom = Number(event.dataTransfer.getData('index'))
    itemsAdd = Number(event.dataTransfer.getData('itemsAdd'))
  }

  const shoppingCart = cartStore.getCart(p.cart)
  if (isCompare) {
    itemsAdd = 0
    itemsAddTo = 0
  }
  else {
    // Only real products (a non-config item that has a config parent) carry the -1; content markers
    // are single items (no parent) and must move on their own.
    const fromItemProduct: boolean = shoppingCart[indexFrom]?.nodeType !== NodeType.nt_Configuration && !!shoppingCart[indexFrom]?.config
    const toItemProduct: boolean = shoppingCart[indexTo]?.nodeType !== NodeType.nt_Configuration && !!shoppingCart[indexTo]?.config
    itemsAdd = fromItemProduct ? itemsAdd - 1 : itemsAdd
    itemsAddTo = toItemProduct ? itemsAddTo - 1 : itemsAddTo
  }

  if (indexFrom > indexTo) {
    for (let i = 0; i <= itemsAdd; i++) {
      cartStore.setResortItemByPos(p.cart, indexFrom + i, indexTo + i)
    }
  }
  else if (indexFrom < indexTo) {
    for (let i = 0; i <= itemsAdd; i++) {
      cartStore.setResortItemByPos(p.cart, indexFrom, indexTo + itemsAddTo)
    }
  }
}

// Share list, save list on server with UUID
async function shareList(): Promise<void> {
  const uuid: string = await saveList()
  shareLink.value = ut_addUrlParams(window.location.href, { [PARAM.cartStorageId]: uuid })
}

// Save list on server
async function saveList(): Promise<string> {
  const args: pfWilo.ExtCmd<ExtCmdType.CART_CREATE>['arg'] = {
    cart: JSON.stringify(cartStore.stripCart(cartStore.getCart(p.cart), false) || '{}'),
  }
  const data: tp.IFetchData = {
    usePost: true,
  }
  const res = await pfinderAPI.fetchData(ExtCmdType.CART_CREATE, args, data)

  let uuid: string = ''
  if (res && res.result) {
    uuid = res.result
  }
  return uuid
}

// Load list from server
async function loadList(uuid: string): Promise<void> {
  const args: pfWilo.ExtCmd<ExtCmdType.CART_READ>['arg'] = {
    uuid,
  }
  const res = await pfinderAPI.fetchData(ExtCmdType.CART_READ, args, {})
  let cart: pf.CartItem[] = []
  if (res && res.result) {
    try {
      // A project saves { items, pdf }, this share link only ever wants the items; anything older is
      // the bare array it always was.
      const parsed: unknown = JSON.parse(res.result.data || '{}')
      cart = Array.isArray(parsed) ? parsed : ((parsed as IStoredProject)?.items || [])
    }
    catch (err) {
      const e = err as Error
      console.error('Error loading cart from uuid:', e.message, uuid)
    }
  }
  cartStore.setCart(p.cart, cart)
  await cartStore.setUpdateCart(p.cart)
  // A shared price list saved before the row ids existed (24.09.2026) gets them now.
  if (isPriceList) { priceList.ensureRowIds() }
}

async function downloadData(type: 'csv' | 'xlsx'): Promise<void> {
  const data: tp.IFetchData = {
    usePost: true,
  }
  const cart = cartStore.getCart(p.cart)
  const args: pfWilo.ExtCmd<ExtCmdType.TABLE_DOWNLOAD>['arg'] = {
    cart: cartStore.stripCart(cart, true),
    type,
    tmpl: 'DWT',
  }
  const res = await pfinderAPI.fetchData(ExtCmdType.TABLE_DOWNLOAD, args, data)

  if (res && res.result && res.result.path) {
    const link = document.createElement('a')
    link.href = runTimeConfig.public.dataHost + '/' + res.result.path
    link.download = link.href.split('/').pop() || ''
    link.click()
    link.remove()
  }
}

async function removeFromCart(isConfig: boolean, namePath?: string, nodeName?: string): Promise<void> {
  await ut_wilo_removeItemFromCart(cartStore, p.cart, isConfig, namePath, nodeName)
  ut_wilo_setAdessoStorage(p.cart, cartStore.getCartCount(p.cart), p.realCountry)
}

function showCart(): boolean {
  let res: boolean = true
  if (ut_isEmbedded() && (isDownloads || isFavorites || isLcc)) {
    res = false
  }
  if (isPriceList && !ut_wilo_showPriceList(p.isInternal)) {
    res = false
  }
  return res
}
</script>

<template>
  <template v-if="showCart()">
    <UContainer>
      <!-- Header -->
      <div v-if="!ut_isPrint() && cart" class="grid md:grid-cols-2 gap-y-4 mb-4">
        <div class="flex items-center">
          <UIcon :name="'i-wilo-' + (!isLcc ? (ECartIconAr[cart]) : 'file-plus-solid-full')" class="shrink-0 size-10 mr-4" />
          <h1 v-html="!isLcc ? ut_getText('def_' + cart + '_txt_page') : 'Booklet creator'" />
        </div>
        <div>
          <div class="-mx-2 flex flex-wrap gap-y-2 gap-x-4 md:justify-end">
            <UButton
              v-if="isCompare"
              variant="shadow"
              color="tertiary"
              icon="i-wilo-file-arrow-down-solid-full"
              label="PDF"
              @click="clickPDF()"
            />
            <UButton
              v-if="!isFavorites && !isLcc"
              icon="i-wilo-share-nodes-regular-full"
              :label="ut_getText('txt_share')"
              variant="shadow"
              color="tertiary"
              @click="shareList"
            />
            <!-- On an open price-list project (a `tab` in the url, `new` included) the settings offer the
                 language only: the markers carry every language since 17.09.2026, so switching it
                 switches the list. Country and the restrictions stay out — they change which series
                 exist and would change the project's contents (hidden completely from 15.09.2026 until
                 then). The project management and every other cart keep the full settings. -->
            <LazySettingsSmall
              :is-historic="false"
              :real-country="realCountry"
              :is-internal="isInternal"
              :show-frequency="false"
              :hide-cart-changing="isPriceList && !showProjects"
            />
          </div>
        </div>
      </div>
    </UContainer>

    <!-- No open project: the page IS the project management. The intro text of the price list stands
         here and only here (24.09.2026) — it invites to start one, which is what this page is for;
         inside an open project it only pushed the list down. -->
    <UContainer v-if="showProjects">
      <p v-if="!ut_isPrint()" class="m-0 mb-8" v-html="ut_getText('txt_intro-' + ut_wilo_getSpecialPageText(cart || ''))" />
      <CartManage
        :cart="cart"
        :opening="opening"
        @release="releaseProject()"
        @open="void openProject($event)"
        @open-saved="void openSaved($event)"
      />
    </UContainer>

    <UContainer v-show="!showProjects">
      <!-- Open price-list project: the way back to the project management, at the top of the page and
           in the look of "select series or products" on catalogue-create (24.09.2026, Christoph) — a
           plain link-like button, not a second action next to "Create price list". Left-pointing,
           because it goes back. -->
      <div v-if="isPriceList && currentProject && !ut_isPrint()" class="my-2">
        <UButton variant="ghost" icon="i-wilo-chevron-left-solid-full" :label="ut_getText('btn_pricelist-to-projects')" @click="void closeProject()" />
      </div>
      <!-- Description — not in an open price-list project; its intro stands on the project management
           above. -->
      <div v-if="!ut_isPrint() && cart && !isPriceList">
        <!-- :class="isCatalog ? 'grid md:grid-cols-2 gap-8' : ''" -->
        <div>
          <p class="m-0" v-html="!isLcc ? ut_getText('txt_intro-' + ut_wilo_getSpecialPageText(cart || '')) : 'This tool helps you create <b>LCC Booklets</b> and <b>Order Booklets</b> quickly and efficiently as standardized PDF documents in the Wilo corporate design - available in all languages, for all subsidiaries, and containing all relevant information, plus additional product details whenever needed.'" />

          <!-- LCC booklet creator -->
          <div v-if="isLcc">
            <p class="mt-2" v-html="'The <b>LCC Booklet</b> contains life cycle cost information<br>In Wilo-Select, choose: Pump exchange (with calculation)'" />
            <p class="mt-2" v-html="'The <b>Order Booklet</b> contains quotation-related information<br>In Wilo-Select, choose: Product Grid'" />
            <p
              class="mt-2"
              v-html="'To create your booklet, first export the corresponding Excel file from Wilo-Select, then upload it to this tool. Based on this Excel file, the system will generate your project-specific PDF booklet.<br>'
                + '<b>Note</b>: Excel files created before <b>1 July 2025</b> are not supported. If your file is older, please download a new Excel export from Wilo-Select and upload the updated file again.'"
            />
            <UButton class="mt-4" icon="i-wilo-chevron-right-solid-full" variant="ghost" to="https://select.wilo.com" target="_blank" label="Wilo-Select" />
          </div>

          <!-- Empty list -->
          <template v-if="!isLcc && !isPriceList">
            <div class="my-2">
              <p v-if="isCatalog" class="mb-4" v-html="ut_getText('txt_list-empty-catalogcreate_new-sys4')" />
              <p v-if="cartStore.getCartCount(cart) === 0" class="mb-4 font-bold" v-html="ut_getText('txt_list-empty')" />
              <ClientOnly>
                <UButton variant="ghost" icon="i-wilo-chevron-right-solid-full" :to="getCatalogPath()">
                  <span v-html="ut_getText('txt_select-more')" />
                </UButton>
              </ClientOnly>
            </div>
          </template>
        </div>

      </div>

      <!-- Lcc upload buttons -->
      <div v-if="isLcc">
        <LazyLcc
          :real-country="realCountry"
          @update:lcc-data="lccData = $event"
          @update:lcc-empty-cart="cartStore.setEmptyCart(cart)"
        />
      </div>
    </UContainer>
    <!-- Cart (no Print) — price-list also renders when empty so its insert ("+") row is available -->
    <!-- v-if, not v-show: with no open project the price list must not render at all. Left visible,
         it falls into the plain cart branch below (CartSeriesPriceList needs a project), and there a
         local marker has no desc — which is exactly what an empty row looks like. -->
    <div v-if="(cartStore.getCartCount(cart) > 0 || isPriceList) && !ut_isPrint() && !showProjects">
      <!-- The price list sits closer to its "To project management" link above (half the gap,
           24.09.2026): mt-5 + the link's my-2 = 1.75rem instead of 3.5rem. -->
      <!-- Price list: half the gap down to the table (15.10.2026, customer) — here and in `mt-4` below. -->
      <UContainer :class="isPriceList ? 'mt-5 mb-6' : 'my-12'">
        <!-- Button action Catalog & Downloads & Price list. (The way back out of a price-list project
             moved to the top of the page on 24.09.2026 — see the description area.) -->
        <div class="flex flex-wrap items-center gap-4">
          <div v-if="(isCatalog || isDownloads || isLcc || (isPriceList && cartStore.getCartCount(cart) > 0)) && cart">
            <div v-for="index in isDownloads ? 2 : 1" :key="index" :class="isDownloads && index === 1 ? 'mb-4' : ''">
              <UButton
                :icon="'i-wilo-' + (ECartIconAr[isLcc ? ECarts.ct_catalogue_create : cart])"
                :label="!isDownloads ? ut_getText('txt_action_' + (isLcc ? 'catalogcreate' : ut_wilo_getSpecialPageText(cart || ''))) : 'Download' + (isDownloads ? index === 1 ? ' (Excel)' : ' (CSV)' : '')"
                @click="!isDownloads ? clickPDF() : downloadData(index === 1 ? 'xlsx' : 'csv')"
              />
            </div>
          </div>
        </div>

        <!-- The name line (CartProjectName). Renamable only once the project is editable. A locked template
             project is not a project of one's own yet — it carries a generated name, is not on the server,
             and offering to rename it would suggest something exists that does not. So it shows the plain
             TEMPLATE name, without pencil (15.10.2026, customer request). An empty project is editable at
             once and therefore shows its name from the start. -->
        <CartProjectName
          v-if="currentProject && currentProject.editable"
          :name="currentProject.name"
          :save-label="saveLabel"
          @rename="void rename($event)"
        />
        <CartProjectName
          v-else-if="currentProject && currentProject.templateName"
          :name="currentProject.templateName"
          save-label=""
          readonly
        />

        <!-- Button action Favorites Share -->
        <template v-if="isFavorites">
          <UButton icon="i-wilo-share-nodes-regular-full-white" :label="ut_getText('txt_share')" @click="shareList" />
        </template>
        <LazyShareModal v-if="shareLink" :share-link="shareLink" :modal-open="true" @update:close="shareLink = ''" />

        <!-- Asked when "Enable edit" turns a template project into one of the user's own. Cancel
             leaves it locked, so nothing is created. -->
        <CartProjectNameModal
          :open="nameAsk"
          :suggestion="currentProject?.name || ''"
          @confirm="confirmName($event)"
          @cancel="nameAsk = false"
        />
      </UContainer>

      <!-- Compare -->
      <template v-if="isCompare">
        <UContainer>
          <div class="flex">
            <CartTableHeader
              class="ml-auto"
              :is-sort-view="isSortView"
              :show-sort="true"
              :show-view="true"
              :compact-view="compactView !== undefined ? compactView : ut_isScreenSize([EScreenSize.sc_xs, EScreenSize.sc_sm, EScreenSize.sc_md])"
              @update:view="compactView = $event"
              @update:sort="toggleSortView($event)"
              @update:emptycart="emptyCart()"
            />
          </div>
        </UContainer>

        <!-- Desktop view -->
        <div v-if="!ut_isScreenSize([EScreenSize.sc_xs, EScreenSize.sc_sm])" class="hidden md:block relative">
          <div class="absolute top-[50px] left-0 w-full h-[6px] bg-gunmetal-4" />
          <UContainer>
            <UTabs
              v-model="selectedTab"
              class="mt-4"
              :items="getCompareTabItems()"
              :unmount-on-hide="false"
              @update:model-value="changeCompareTab()"
            >
              <template v-if="!isSortView" #content="{ item }">
                <!-- Dutypoint -->
                <div v-if="item.value === ECompareType.products" class="flex flex-wrap gap-x-16 gap-y-8 my-4">
                  <InputDutyPoint
                    :real-country="realCountry"
                    :set-url="true"
                    :duty-point-input="dutyPointInput"
                    @update:duty-point="dutyPointInput = $event"
                  />
                </div>
                <!-- Table -->
                <LazyCompare
                  v-if="compareTable[item.value || '']"
                  class="mt-8"
                  :table-node="compareTable[item.value || '']"
                  :country-language="countryLanguage"
                  :is-compare="true"
                  :is-product="item.value === ECompareType.products"
                  :is-internal="isInternal"
                  :real-country="realCountry"
                  :duty-point-input="dutyPointInput"
                  :duty-point-is-set="!!dutyPointInput.DutyPoint?.Flow"
                  :compact-view="compactView !== undefined ? compactView : ut_isScreenSize([EScreenSize.sc_xs, EScreenSize.sc_sm, EScreenSize.sc_md])"
                  @update:remove="removeFromCart(false, '', $event)"
                />
              </template>
            </UTabs>
          </UContainer>
        </div>

        <!-- Mobile view -->
        <div v-else class="block md:hidden">
          <UContainer>
            <UAccordion
              v-model="selectedAccordion"
              class="mt-4"
              trailing-icon="i-wilo-xmark-solid-full"
              :items="getCompareTabItems() as AccordionItem[]"
              :unmount-on-hide="false"
              @update:model-value="changeCompareAccordion()"
            >
              <template v-if="!isSortView" #content="{ item }">
                <!-- Dutypoint -->
                <div v-if="item.value === ECompareType.products" class="flex flex-wrap gap-x-16 gap-y-8 my-4">
                  <InputDutyPoint
                    :real-country="realCountry"
                    :set-url="true"
                    :duty-point-input="dutyPointInput"
                    @update:duty-point="dutyPointInput = $event"
                  />
                </div>
                <!-- Table -->
                <LazyCompare
                  v-if="compareTable[item.value || '']"
                  class="mt-8"
                  :table-node="compareTable[item.value || '']"
                  :country-language="countryLanguage"
                  :is-compare="true"
                  :is-product="item.value === ECompareType.products"
                  :is-internal="isInternal"
                  :real-country="realCountry"
                  :duty-point-input="dutyPointInput"
                  :duty-point-is-set="!!dutyPointInput.DutyPoint?.Flow"
                  :compact-view="compactView !== undefined ? compactView : ut_isScreenSize([EScreenSize.sc_xs, EScreenSize.sc_sm, EScreenSize.sc_md])"
                  @update:remove="removeFromCart(false, '', $event)"
                />
              </template>
            </UAccordion>
          </UContainer>
        </div>
      </template>

      <!-- Favorites, Catalog, Downloads, LCC -->
      <template v-else>
        <UContainer>
          <div class="flex" :class="isPriceList ? 'mt-4' : 'mt-8'">
            <!-- Not for the price list (24.09.2026): the count says nothing there — the rows are a
                 project with its own structure, not a selection. -->
            <div v-if="!isPriceList" class="flex flex-wrap gap-2">
              <div>
                <b v-html="ut_getText('txt_my-selection')" />
                <span v-html="' | '" />
              </div>
              <div>
                <div v-html="ut_getText('txt_selection-series') + ': ' + cartConfigs().length" />
              </div>
              <div v-if="!isDownloads">
                <div v-html="' | ' + ut_getText('txt_selection-products') + ': ' + (cartStore.getCartCount(cart) - cartConfigs().length)" />
              </div>
            </div>
            <div class="ml-auto">
              <CartTableHeader
                :is-sort-view="isSortView"
                :show-view="false"
                :show-sort="cartConfigs().length > 1"
                :hide-empty="isPriceList"
                @update:sort="toggleSortView($event)"
                @update:emptycart="emptyCart()"
              />
            </div>
          </div>
          <!-- Normal cart view -->
          <template v-if="!isSortView">
            <div class="mt-4 overflow-auto">
              <table class="w-full">
                <thead />
                <tbody>
                  <!-- Price-list cart: the whole body lives in CartSeriesPriceList (own editors/formatting). -->
                  <LazyCartSeriesPriceList
                    v-if="isPriceList && currentProject"
                    :cart="cart"
                    :real-country="realCountry"
                    :country-language="countryLanguage"
                    :is-internal="isInternal"
                    :editable="currentProject.editable"
                    @update:editable="setEditable($event)"
                  />
                  <!-- Normal cart (catalog / lcc): plain rows. -->
                  <template v-else>
                    <tr
                      v-for="item in cartStore.getCart(cart)"
                      :key="item.namePath"
                      class="odd:bg-gunmetal-5 even:bg-gunmetal-4"
                    >
                      <!-- Desc -->
                      <td class="font-bold text-left hyphens-auto p-4" :class="item.nodeType === NodeType.nt_Configuration ? 'css-xl' : ''">
                        <NuxtLink :to="cartStore.linkPath(item.slugPath ||'')">
                          <span v-html="item.desc" />
                        </NuxtLink>
                      </td>
                      <!-- Article No -->
                      <td class="text-left hyphens-auto p-4">
                        <span v-if="item.nodeType !== NodeType.nt_Configuration && item.attrs" v-html="item.attrs['id_product']" />
                      </td>
                      <!-- Dutypoints for cart item -->
                      <td v-if="isCatalog || isLcc" class="text-left hyphens-auto p-4">
                        <LazyCartItemsDutyPoint
                          v-if="item.nodeType !== NodeType.nt_Configuration && ut_wilo_getCurveId(item.attrs)"
                          :name-path="item.namePath"
                          :prod-desc="item.desc || ''"
                          :real-country="realCountry"
                          :country-language="countryLanguage"
                          :is-internal="isInternal"
                          :duty-point-input-ar="item.userDefined ? item.userDefined[ECartUserDefined.cart_us_dutyPoint] || [] : []"
                          @update:duty-point-ar="cartStore.setUpdateUserDefined(p.cart, item.namePath, $event, ECartUserDefined.cart_us_dutyPoint)"
                        />
                      </td>
                      <td class="text-left hyphens-auto p-4 w-[1px]">
                        <UButton icon="i-wilo-trash-solid-full" variant="ghost" color="tertiary" @click="removeFromCart(item.nodeType === NodeType.nt_Configuration, item.namePath)" />
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </template>
        </UContainer>
      </template>
    </div>

    <!-- Cart Compare (Print) -->
    <template v-if="cartStore.getCartCount(cart) > 0 && isCompare && ut_isPrint()">
      <template v-for="(partTable, index) in ut_wilo_compareTablePrintGroups(compareTable[selectedTab], def_printCompareGroupSize)" :key="index">
        <Compare
          :table-node="partTable"
          :country-language="countryLanguage"
          :is-compare="true"
          :is-product="selectedTab === ECompareType.products"
          :is-internal="isInternal"
          :real-country="realCountry"
          :duty-point-input="dutyPointInput"
          :duty-point-is-set="!!dutyPointInput.DutyPoint?.Flow"
          :start-index="index * def_printCompareGroupSize"
        />
      </template>
    </template>

    <!-- Sort view -->
    <template v-if="isSortView">
      <UContainer>
        <div class="mt-4 overflow-auto">
          <table class="w-full">
            <thead />
            <tbody>
              <tr
                v-for="(config, index) of sortItems()"
                :key="cartRowKey(config)"
                :class="isPriceList ? sortRowColor(config) : 'odd:bg-gunmetal-5 even:bg-gunmetal-4'"
                :draggable="!dragInProgress"
                @dragstart="CompanyStartDrag($event, realIndex(config), config.amount || 0)"
                @touch="CompanyStartDrag($event, realIndex(config), config.amount || 0)"
                @drop="CompanyOnEnter($event, realIndex(config), config.amount || 0, -1, -1)"
                @dragover.prevent
                @dragenter.prevent
              >
                <td class="font-bold css-xl text-left hyphens-auto p-4">
                  <div class="flex items-center gap-2">
                    <UIcon v-if="sortRowIcon(config)" :name="sortRowIcon(config)" class="size-5 shrink-0" />
                    <div v-html="config.desc" />
                  </div>
                </td>
                <td class="px-4 w-[1px]">
                  <div>
                    <UButton
                      icon="i-wilo-sort-up-solid"
                      variant="ghost"
                      :disabled="index < 1"
                      @click="void (index > 0 ? CompanyOnEnter(undefined, realIndex(sortItems()[index - 1]), sortItems()[index - 1]?.amount || 0, realIndex(config), config.amount || 0) : '')"
                    />
                  </div>
                  <div>
                    <UButton
                      icon="i-wilo-sort-down-solid"
                      variant="ghost"
                      :disabled="index === sortItems().length -1"
                      @click="void (index < sortItems().length -1 ? CompanyOnEnter(undefined, realIndex(sortItems()[index + 1]), sortItems()[index + 1]?.amount || 0, realIndex(config), config.amount || 0) : '')"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </UContainer>
    </template>

    <LazyModalPDF
      v-if="showModalPdf"
      :modal-open="showModalPdf"
      :is-cart="isCatalog || isLcc || isPriceList"
      :is-price-list="isPriceList"
      :project-name="isPriceList ? (currentProject?.name || '') : ''"
      :pdf-settings="isPriceList && currentProject ? (currentProject.pdf || {}) : undefined"
      :is-lcc="isLcc"
      :cart-configs="cartConfigsForPdf()"
      :cart-storage-id="cartStorageId"
      :lcc-data="lccData"
      :cart="cartStore.getCart(cart)"
      :special-title="isCompare ? ut_getText('def_' + cart + '_txt_page') : ''"
      :is-compare="isCompare"
      :compare-type="selectedTab"
      :is-internal="isInternal"
      :real-country="realCountry"
      :test-mode="false"
      :extra-keys="(isCompare && selectedTab === 'products') ? extraKeys : []"
      :duty-point-input="dutyPointInput"
      @update:close="showModalPdf = false"
      @update:pdf-settings="currentProject && (currentProject.pdf = $event)"
    />
  </template>
</template>
